/**
 * LIFF SDK initialization and authentication helpers.
 *
 * Handles LIFF SDK init, ID token retrieval, and LIFF login flow.
 *
 * Requirements: 1.1, 25.1
 */

import liff from '@line/liff';

export interface LiffInitResult {
  isLoggedIn: boolean;
  idToken: string | null;
}

let initialized = false;
let initPromise: Promise<LiffInitResult> | null = null;

/**
 * Initializes the LIFF SDK with the configured LIFF ID.
 * Returns whether the user is logged in and their ID token if available.
 * Caches the initialization promise to prevent multiple init calls.
 */
export function initLiff(): Promise<LiffInitResult> {
  if (initPromise) return initPromise;

  const liffId = import.meta.env.VITE_LIFF_ID;

  if (!liffId) {
    initPromise = Promise.resolve({ isLoggedIn: false, idToken: null });
    return initPromise;
  }

  initPromise = liff
    .init({ liffId })
    .then(() => {
      initialized = true;

      if (!liff.isLoggedIn()) {
        // In LIFF environment, trigger auto-login (Requirement 25.1)
        liff.login();
        return { isLoggedIn: false, idToken: null };
      }

      const idToken = liff.getIDToken();
      if (!idToken) {
        // Logged into LINE but no ID token issued — almost always means the
        // LIFF app is missing the "openid" scope (or it wasn't consented).
        // Without an ID token we cannot exchange for a FindoryHub session, so
        // the user appears logged out inside LINE.
        console.warn(
          '[LIFF] isLoggedIn but getIDToken() returned null. ' +
            'Enable the OpenID Connect (openid) scope on the LIFF app and re-consent.',
        );
      }
      return { isLoggedIn: true, idToken };
    })
    .catch((error: unknown) => {
      console.error('[LIFF] Initialization failed:', error);
      initPromise = null; // Allow retry on failure
      return { isLoggedIn: false, idToken: null };
    });

  return initPromise;
}

/**
 * Gets the current LIFF ID token if the user is logged in.
 * Returns null if LIFF is not initialized or user is not logged in.
 */
export function getLiffIdToken(): string | null {
  if (!initialized) return null;
  try {
    return liff.getIDToken();
  } catch {
    return null;
  }
}

/**
 * Checks if LIFF SDK has been successfully initialized.
 */
export function isLiffInitialized(): boolean {
  return initialized;
}

/**
 * Triggers LIFF login. Used when the user needs to re-authenticate.
 */
export function liffLogin(): void {
  if (initialized) {
    liff.login();
  }
}

/**
 * Triggers LIFF logout.
 */
export function liffLogout(): void {
  if (initialized && liff.isLoggedIn()) {
    liff.logout();
  }
}

/**
 * Opens a URL in the device's external system browser via the LIFF SDK.
 *
 * Inside the LINE in-app browser, anchor tags with `target="_blank"` re-launch
 * a fresh LIFF app load, which re-runs LIFF init / login and can trap the user
 * in a "Loading..." loop. `liff.openWindow({ external: true })` hands the URL to
 * the OS browser instead, leaving the current LIFF view untouched.
 *
 * Returns true if the LIFF path was used, false if the caller should fall back
 * to normal browser navigation (e.g. window.open).
 */
export function openExternal(url: string): boolean {
  if (!initialized) return false;
  try {
    liff.openWindow({ url, external: true });
    return true;
  } catch (error) {
    console.error('[LIFF] openWindow failed:', error);
    return false;
  }
}

/**
 * Gets the LIFF user profile if available.
 */
export async function getLiffProfile(): Promise<{
  userId: string;
  displayName: string;
  pictureUrl?: string;
} | null> {
  if (!initialized || !liff.isLoggedIn()) return null;
  try {
    const profile = await liff.getProfile();
    return {
      userId: profile.userId,
      displayName: profile.displayName,
      pictureUrl: profile.pictureUrl,
    };
  } catch {
    return null;
  }
}
